import React, { useContext, useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';

import { AppContext } from '../../App';

import InputField from '../components/InputField';
import SelectField from '../components/SelectField';
import TextAreaField from '../components/TextAreaField';
import CheckboxField from '../components/CheckboxField';
import UploadField from '../components/UploadField';
import RadioField from '../components/RadioField';
import CustomDatePicker from '../components/CustomDatePicker';
import StepProgressBar from '../components/StepProgressBar';
import Modal from '../components/Modal';

const CourseApplication = () => {
  const { courseId } = useParams();
  const appContext = useContext(AppContext);
  const [formHidden, setFormHidden] = useState(false);

  useEffect(() => {
    // Fetch student's course status
    appContext.httpClient.get({
      path: "courseapplication/myapplications",
      authRedirectPath: appContext.loginPath
    }).then(response => {
      if (response.success && response.result) {        
        const course = response.result.find(c => c.courseId == courseId);
        const hiddenStatuses = ['ACCEPTED', 'ENROLLED', 'PENDING', 'BANNED'];
        
        if (course) {
          if (hiddenStatuses.includes(course.statusCodeName)) {
            setFormHidden(true);
          } else {
            setFormHidden(false);
          }
        } else {
          setFormHidden(false);
        }
      }
    });
  }, [appContext.apiBaseUrl, courseId]);

  useEffect(() => {
      if (courseId && !isInitRequested) {
        setIsInitRequested(true);
          appContext.httpClient.get({
              path: "courseapplication/formdata/" + courseId,
              authRedirectPath: appContext.loginPath
          }).then(response => {
              console.log(response.result)
              if (response.result) {     
                const course = response.result.course;
                console.log(response.result);
                setFormData(response.result);
                setAreAllModulesRequired(course.allModulesRequired);
                setNumberOfEducationFields(course.courseEducation);
                setNumberOfProfessionalQualificationFields(course.courseQualifications)

                if (Array.isArray(course.entryCriteria) && course.entryCriteria.length) {                  
                  setValue("entryCriteria", course.entryCriteria);
                }

                const generateEducationFields = (numFields) => {
                  return Array.from({ length: numFields }, () => ({
                    degree: '',
                    yearAwarded: '',
                    collegeUniversity: '',
                    languageOfInstruction: ''
                  }));
                };

                const generateProfessionalQualificationsFields = (numFields) => {
                  return Array.from({ length: numFields }, () => ({
                    qualificationDescription: '',
                    qualificationDate: '',
                    profession: '',
                    practicing: ''
                  }));
                };
                
                // Example usage:
                const courseEducation = response.result.course.courseEducation; // This value could be dynamic
                const educationDefaultValues = generateEducationFields(courseEducation);
                setValue('educationHistory', educationDefaultValues);

                // Doing the same for professional quals:
                const courseQualifications = response.result.course.courseQualifications; // This value could be dynamic
                const professionalQualificationsDefaultValues = generateProfessionalQualificationsFields(courseQualifications);
                setValue('qualifications', professionalQualificationsDefaultValues);
              }
          });
      }  
  }, [appContext.apiBaseUrl]);

  

  const { register, handleSubmit, control, getValues, setValue, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      workExperience: [{
        company: '',
        companyAddress1: '',
        companyAddress2: '',
        companyCity: '',
        companyState: '',
        companyPostcode: '',
        companyCountry: '',
        jobTitle: '',
        typeOfOrganisationId: 0,
        typeOfOrganisationOther: '',
        startDate: null,
        jobDescription: ''
      }],
      educationHistory: [],
      entryCriteria: [],
      qualifications: [{
        qualificationDescription: '',
        qualificationDate: null,
        profession: ''
      }],
      references: [{}, {}],
      paymentMethod: 'CARD'
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [areAllModulesRequired, setAreAllModulesRequired] = useState(true);
  const [isDifferentEmailChecked, setDifferentEmailChecked] = useState(false);
  const [isPostalAddressChecked, setPostalAddressChecked] = useState(false);
  const [isWhoWillPayOther, setWhoWillPayOther] = useState(false);
  const [isTypeOfOrganisationOther, setIsTypeOfOrganisationOther] = useState([]);
  //const [isDegreeUploadChecked, setDegreeUploadChecked] = useState(false);
  const [isIeltsOrToeflChecked, setIeltsOrToeflChecked] = useState(false);
  const [numberOfEducationFields, setNumberOfEducationFields] = useState(1);
  const [numberOfProfessionalQualificationFields, setNumberOfProfessionalQualificationFields] = useState(1);
  const [module2, setModule2] = useState(null);
  const [step, setStep] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showFormErrorModal, setShowFormErrorModal] = useState(false);
  const [formError, setFormError] = useState(false);
  const [showPaymentPreviewModal, setShowPaymentPreviewModal] = useState(false);
  const [paymentPreview, setPaymentPreview] = useState(false);
  const [confirmPaymentPreview, setConfirmPaymentPreview] = useState(false);
  const [isInitRequested, setIsInitRequested] = useState(false);
  const [formData, setFormData] = useState({
    personTitles: [],
    countries: [],
    fundingTypes: [],
    genders: [],
    organisationTypes: [],
    course: {
      courseTitle: ''
    }
  });
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isBursaryDiscountChecked, setBursaryDiscountChecked] = useState(false);

  

  const formRef = useRef(null);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [step]);

  const {
    fields: companyFields,
    append: appendCompany,
    remove: removeCompany
  } = useFieldArray({
    control,
    name: 'workExperience'
  });

  const {
    fields: educationFields,
    append: appendEducation,
    remove: removeEducation
  } = useFieldArray({
    control,
    name: 'educationHistory'
  });

  const {
    fields: professionalFields,
    append: appendProfessional,
    remove: removeProfessional
  } = useFieldArray({
    control,
    name: 'qualifications'
  });

  const {
    fields: entryCriteriaFields
  } = useFieldArray({
    control,
    name: 'entryCriteria'
  });

  const {
    fields: referencesFields
  } = useFieldArray({
    control,
    name: 'references'
  });

  const handleDifferentEmailChecked = (e) => {
    setDifferentEmailChecked(e.target.checked);
  };

  const handlePostalAddressChecked = (e) => {
    setPostalAddressChecked(e.target.checked);
  };

  const handleWhoWillPayOther = (e) => {
    const selectedValue = e.target.value;
    setWhoWillPayOther(selectedValue);
    setValue("fundingId", selectedValue, { shouldValidate: true });
  };

  const handleTypeOfOrganisationOther = (e, index) => {
    const selectedValue = e.target.value;
    const updatedState = [...isTypeOfOrganisationOther];
    updatedState[index] = e.target.getAttribute("data-code-name") === 'OTHER';
    setIsTypeOfOrganisationOther(updatedState);
    setValue(`workExperience[${index}].typeOfOrganisationId`, selectedValue, { shouldValidate: true });
  };

  const handleEntryCriteriaChecked = (e, index) => {
    const { name, checked } = e.target;

    // Get the current values of both checkboxes
    const otherOptionName = name.endsWith('.option1') ? `entryCriteria[${index}].option2` : `entryCriteria[${index}].option1`;

    // Uncheck the other option
    setValue(otherOptionName, false);
    // Update the value of the clicked checkbox
    setValue(name, checked);
};

  const handleIeltsOrToeflChecked = (e) => {
    setIeltsOrToeflChecked(e.target.checked);
  };

  const handleModule2AChange = () => {
      setModule2(module2 === '2A' ? null : '2A');
      console.log(module2)
  };

  const handleModule2BChange = () => {
    setModule2(module2 === '2B' ? null : '2B');
    console.log(module2)
  };

  const handlePaymentMethod = (e) => {
    setPaymentMethod(e.target.value);
  };

  const handleBursaryDiscount = (e) => {
    setBursaryDiscountChecked(e.target.checked);
  };

  const handleCloseFormErrorModal = () => {
    setShowFormErrorModal(false);
  };

  const handleClosePaymentPreviewModal = () => {
    setShowPaymentPreviewModal(false);
  };

  const handleContinuePayment = () => {
    setConfirmPaymentPreview(true);
    setShowPaymentPreviewModal(false);
  };

  const addCompany = () => {
    appendCompany({
      company: '',
      companyAddress1: '',
      companyAddress2: '',
      companyCity: '',
      companyState: '',
      companyPostcode: '',
      companyCountry: '',
      jobTitle: '',
      typeOfOrganisationId: 0,
      typeOfOrganisationOther: '',
      startDate: null,
      jobDescription: ''
    });
  };

  const addEducation = () => {
    appendEducation({
      description: '',
      yearAwarded: '',
      collegeUniversity: '',
      language: ''
    });
  };

  const addProfessional = () => {
    appendProfessional({
      qualificationDescription: '',
      qualificationDate: '',
      profession: '',
      practicing: false
    });
  };

  const getErrorMessage = (list, index, field) => {
    return errors[list] &&
    errors[list].length > index &&
    errors[list][index] &&
    errors[list][index][field] &&
    errors[list][index][field].message;
  }

  const onSubmit = async (data) => {
    const userId = appContext.userId;

    const revisedData = {
      ...data,
      courseId,
      userId,
      // assume we need to pass payment method here
    }

    console.log("courseId" + courseId)
    console.log("userId" + userId)
    console.log(revisedData);

    // Only proceed if there are no validation errors
    const formErrors = Object.keys(errors).length > 0;

    if (formErrors) {
      console.log("Form has errors:", errors);
      return;
    }
  
    // Only proceed with payment if the current step is "Payment"
    if (filteredSteps[step].title === "Payment") {

      console.log(revisedData);

      if (revisedData.entryCriteria?.length) {
        revisedData.entryCriteria.forEach(ec => {
          if (ec.selection === "1") ec.selection = 1;
          else if (ec.selection === "2") ec.selection = 2;
        })
      }

      if (revisedData.qualifications?.length) {
        revisedData.qualifications.forEach(q => q.practicing = q.practicing === true || q.practicing === "true")
      }

      console.log(revisedData);
      setIsSubmitting(true);
      try {

        if(!confirmPaymentPreview){
          // First, get payment information
          const paymentResponse = await appContext.httpClient.post({
            path: "courseapplication/paymentinfo",
            body: revisedData
          });

          if (paymentResponse.success && paymentResponse.result) {
            console.log("Payment Info:", paymentResponse.result);
            setPaymentPreview(paymentResponse.result)
            setShowPaymentPreviewModal(true)
            console.log("Show Payment Preview Modal:", showPaymentPreviewModal);
          } else {
            console.log("payment info error")
            debugger;
          }
        }

        if(confirmPaymentPreview) {

          // Post the form data to the server
          const response = await appContext.httpClient.post({
            path: "courseapplication",
            body: revisedData
          });
    
          // Check if the response was successful
          if (response.success) {
            console.log("Form submitted successfully:", response);
            setIsSubmitted(true);
          } else {
            debugger;

            if (response.statusCode === 422 && response.validationResult?.errors?.USER_ALREADY_APPLIED) {
                // handle when the user has already applied
                debugger;
            }

            // Handle server-side validation or processing errors
            console.log("Error submitting the form:", response.message || "Unknown error occurred.");
            // Display error message to the user (optional)
            setFormError(response.message || "An error occurred while submitting the form.");
            setShowFormErrorModal(true);
          }

        }
        
      } catch (error) {
        // Handle network or other unexpected errors
        console.error("Submission failed due to a network or server error:", error);
        setFormError("A network error occurred. Please try again later.");
        setShowFormErrorModal(true);
      } finally {
        setIsSubmitting(false);
      }
  
    } else {
      // Move to the next step if not on the payment step
      console.log("Moving to next step");
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setIsSubmitted(false);
    console.log("Current step before going back:", step);

    if (step > 0) {
        const newStep = step - 1;
        setStep(newStep);
        console.log("Updated step:", newStep);
    } else {
        console.log("Attempted to go back from the first step.");
    }
  };

  const [checkedEntryCriteria, setCheckedEntryCriteria] = useState({});

  const handleEntryCriteriaChange = (e, index) => {
      const { name, value, checked, type } = e.target;

      if (type === 'checkbox') {
        setCheckedEntryCriteria((prevState) => ({
          ...prevState,
          [index]: checked
        }));
        setValue(`entryCriteria[${index}].selection`, checked ? 1 : 0, { shouldValidate: true });
      } else if (type === 'radio') {
        // If it's a radio button, track the selected option value
        setCheckedEntryCriteria((prevState) => ({
          ...prevState,
          [index]: value
        }));
        setValue(`entryCriteria[${index}].selection`, value, { shouldValidate: true });
      }

      console.log(`Changed field at index ${index}:`, { name, value, checked });
  };

  const steps = [
    {
      title: "Personal Information",
      content: (
        <>
          <h2 className="text-xl text-brandDarkBlue font-bold mb-6">About you</h2>
            <InputField
                label="Title"
                name="title"
                register={register}
                rules={{ required: "Title is required" }}
                error={errors.title?.message}
            />
            <InputField 
                label="First name"
                name="firstName"
                register={register}
                rules={{ required: "First name is required" }}
                error={errors.firstName?.message}
            />
            <InputField 
                label="Middle name"
                name="middleName"
                register={register}
            />
            <InputField 
                label="Last name"
                name="lastName"
                register={register}
                rules={{ required: "Last name is required" }}
                error={errors.lastName?.message}
            />
            <InputField 
                label="Certificate name"
                name="certificateName"
                explanation="Name as you would like it to appear on your certificate"
                register={register}
            />
            <InputField 
                label="Email"
                name="email"
                register={register}
                rules={{ 
                  required: "Email is required",
                  pattern: { value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, message: "Email address must be a valid email address" }
                }}
                error={errors.email?.message}
            />
            <CheckboxField
                label="Send course emails to different email address"
                name="courseEmailsDifferentEmailAddress"
                register={register}
                onChange={handleDifferentEmailChecked}
            />
            {isDifferentEmailChecked && (
              <InputField 
                  label="Email to receive course notifications"
                  name="courseCorrespondenceEmail"
                  register={register}
                  rules={{ 
                    required: isDifferentEmailChecked ? "Email notifications address is required" : false,
                    pattern: { value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, message: "Email address must be a valid email address" }
                  }}
                  error={errors.emailNotifications?.message}
              />
            )}
            <InputField 
                label="Phone"
                name="phone"
                register={register}
            />
            <InputField 
                label="Mobile"
                name="mobile"
                register={register}
            />
            <SelectField
                label="Gender"
                name="genderId"
                defaultValue=""
                register={register}
                rules={{ required: "Gender is required" }}
                error={errors.gender?.message}
                options={formData.genders}
                valueField="itemId"
                textField="description"
            />
            <SelectField
                label="Nationality"
                name="nationalityId"
                defaultValue=""
                register={register}
                rules={{ required: "Nationality is required" }}
                error={errors.nationality?.message}
                options={formData.countries}
                valueField="countryId"
                textField="countryDisplayName"
            />
            <SelectField
                label="Country of residence"
                name="countryOfResidenceId"
                defaultValue=""
                register={register}
                rules={{ required: "Country of residence is required" }}
                options={formData.countries}
                valueField="countryId"
                textField="countryDisplayName"
            />
            <hr className="mt-6 border-t border-dashed" />
            <h2 className="text-xl text-brandDarkBlue font-bold my-6">Address</h2>
            <InputField 
                label="Home Address 1"
                name="homeAddress1"
                register={register}
                rules={{ required: "Home Address 1 is required" }}
                error={errors.homeAddress1?.message}
            />
            <InputField 
                label="Home Address 2"
                name="homeAddress2"
                register={register}
            />
            <InputField 
                label="City"
                name="homeCity"
                register={register}
                rules={{ required: "City is required" }}
                error={errors.homeCity?.message}
            />
            <InputField 
                label="State"
                name="homeState"
                register={register}
            />
            <InputField 
                label="Postcode"
                name="homePostcode"
                register={register}
                rules={{ required: "Postcode is required" }}
                error={errors.homePostcode?.message}
            />
            <CheckboxField
                label="Send course certificate to different address"
                name="courseCertificateAddress"
                type="checkbox"
                register={register}
                onChange={handlePostalAddressChecked}
            />
            {isPostalAddressChecked && (
              <>
              <InputField 
                  label="Postal Address 1"
                  name="postalAddress1"
                  register={register}
              />
              <InputField 
                  label="Postal Address 2"
                  name="postalAddress2"
                  register={register}
              />
              <InputField 
                  label="Postal City"
                  name="postalCity"
                  register={register}
              />
              <InputField 
                  label="Postal Postcode"
                  name="postalPostcode"
                  register={register}
              />
              </>
            )}
            <hr className="mt-6 border-t border-dashed" />
            <h2 className="text-xl text-brandDarkBlue font-bold my-6">Personal statement</h2>
            <TextAreaField 
                label="In no more than 250 words, please explain why you want to enrol for the course and what you hope to gain by doing so"
                name="personalStatement"
                register={register}
                rules={{
                  required: "Personal statement is required",
                  validate: {
                    maxWords: (value) => {
                        const wordCount = value.trim().split(/\s+/).length;
                        return wordCount <= 250 || "Personal statement must be no more than 250 words";
                    }
                  }
                }}
                error={errors.personalStatement?.message}
            />
          </>
      )
    },
    {
      title: "Entry Criteria",
      conditional: formData?.course?.entryCriteria && formData.course.entryCriteria.length > 0,
      content: (
        <>
          <p className="text-brandMidBlue text-md mb-4">Please note that applicants must meet all the entry criteria before they will be allowed to enrol on the course.</p>
          <p className="text-brandMidBlue text-md mb-4">Please confirm each of the following statements by clicking the relevant box:</p>
          {entryCriteriaFields.map((item, index) => {
            return (
              <div data-id={item.courseEntryCriteriaId} key={item.courseEntryCriteriaId}>
                {!item.isDouble ? (
                  <>
                    <CheckboxField
                      name={`entryCriteria[${index}].selection`}
                      label={item.statement}
                      explanation={item.clarification}
                      register={register}
                      value={1}
                      rules={{ required: "Entry criteria is required" }}
                      error={getErrorMessage("entryCriteria", index, "selection")}
                      onChange={(e) => handleEntryCriteriaChange(e, index)}
                    />
                    {checkedEntryCriteria[index] && item.fileUploadRequired && (
                      <UploadField 
                        label="Upload"
                        name={`entryCriteria[${index}].file`}
                        setValue={setValue}
                        register={register}
                        rules={!item.fileCanBeSentLater ? { required: 'This file is required' } : undefined}
                        error={!item.fileCanBeSentLater ? getErrorMessage("entryCriteria", index, "file_file") : undefined}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <RadioField 
                      label="Please select which entry criteria you meet"
                      name={`entryCriteria[${index}].selection`}
                      register={register}
                      options={[
                        { value: 1, label: item.statement, explanation: item.clarification },
                        { value: 2, label: item.statement2, explanation: item.clarification2 }
                      ]}
                      rules={{ required: "Entry criteria is required" }}
                      error={getErrorMessage("entryCriteria", index, "selection")}
                      onChange={(e) => handleEntryCriteriaChange(e, index)}
                    />
                    {checkedEntryCriteria[index] === '1' && item.fileUploadRequired && (
                      <UploadField 
                        label="Upload"
                        name={`entryCriteria[${index}].file`}
                        setValue={setValue}
                        register={register}
                        rules={{ required: 'This file is required' }}
                        error={getErrorMessage("entryCriteria", index, "file_file")}
                      />
                    )}
                    {checkedEntryCriteria[index] === '2' && item.fileUploadRequired2 && (
                      <UploadField 
                        label="Upload"
                        name={`entryCriteria[${index}].file`}
                        setValue={setValue}
                        register={register}
                        rules={!item.fileCanBeSentLater2 ? { required: 'This file is required' } : undefined}
                        error={!item.fileCanBeSentLater2 ? getErrorMessage("entryCriteria", index, "file_file") : undefined}
                      />
                    )}
                  </>
                )}
                {entryCriteriaFields.length > index + 1 && <hr className="my-6 border-t border-dashed" />}
              </div>
            )
            })}
        </>
      )
    },
    {
      title: "Employment Details",
      content: (
        <>
            {companyFields.map((item, index) => (
              <div key={"work-experience-" + index}>
                <div className="flex justify-between items-center">
                  <h2 className="text-xl text-brandDarkBlue font-bold my-6">Company {index + 1}</h2>
                  {index > 0 && (
                    <button type="button" onClick={() => removeCompany(index)} className="text-red underline font-medium whitespace-nowrap">
                      Remove
                    </button>
                  )}
                </div>
                <InputField 
                    label="Company"
                    name={`workExperience[${index}].company`}
                    register={register}
                    rules={{ required: "Company name is required" }}
                    error={errors?.workExperience?.[index]?.company?.message}
                />
                <InputField 
                    label="Company Address 1"
                    name={`workExperience[${index}].companyAddress1`}
                    register={register}
                />
                <InputField 
                    label="Company Address 2"
                    name={`workExperience[${index}].companyAddress2`}
                    register={register}
                />
                <InputField 
                    label="Company City"
                    name={`workExperience[${index}].companyCity`}
                    register={register}
                />
                <InputField 
                    label="Company State"
                    name={`workExperience[${index}].companyState`}
                    register={register}
                />
                <InputField 
                    label="Company Postcode"
                    name={`workExperience[${index}].companyPostcode`}
                    register={register}
                />
                <SelectField
                    label="Company Country"
                    name={`workExperience[${index}].companyCountryId`}
                    defaultValue=""
                    register={register}
                    rules={{ required: "Company Country is required" }}
                    options={formData.countries}
                    valueField="countryId"
                    textField="countryDisplayName"
                    error={errors?.workExperience?.[index]?.companyCountryId?.message}
                />
                <InputField 
                    label="Job title"
                    name={`workExperience[${index}].jobTitle`}
                    register={register}
                />
                <RadioField 
                    label="Type of organisation"
                    name={`workExperience[${index}].typeOfOrganisationId`}
                    register={register}
                    index={index}
                    options={formData.organisationTypes.map(ot => {return { value: ot.itemId, label: ot.description, codeName: ot.codeName }})}
                    onChange={(e) => handleTypeOfOrganisationOther(e, index)}
                    rules={{ required: "The type of organisation is required" }}
                    error={errors?.workExperience?.[index]?.typeOfOrganisationId?.message}
                />
                {isTypeOfOrganisationOther[index] && (
                  <InputField 
                      label="Please specify type of organisation"
                      name={`workExperience[${index}].otherTypeOfOrganisation`}
                      register={register}
                      rules={{
                        required: isTypeOfOrganisationOther[index] ? "Please specify type of organisation" : false,
                      }}
                      error={errors?.workExperience?.[index]?.otherTypeOfOrganisation?.message}
                  />
                )}
                <CustomDatePicker
                    label="Start Date"
                    name={`workExperience[${index}].startDate`}
                    register={register}
                    selectedDate={watch(`workExperience[${index}].startDate`)}
                    setValue={setValue}
                />
                <TextAreaField 
                    label="In no more than 250 words, please explain what your work and responsibilities were in this role"
                    name={`workExperience[${index}].jobDescription`}
                    register={register}
                    rules={{ required: "Work and responsibilities is required", max: 250 }}
                    error={errors?.workExperience?.[index]?.workResponsibilities?.message}
                />
                <hr className="my-6 border-t border-dashed" />
              </div>
            ))}
            <button type="button" onClick={addCompany} className="bg-brandMidBlue hover:bg-blue-700 text-white font-medium mb-6 py-2 px-6 rounded">
              Add Another
            </button>
            <InputField 
                label="Years in Insolvency and turnarounds"
                name="industryExperienceYears"
                register={register}
                rules={{ 
                  required: "Years in Insolvency and turnarounds is required",
                  pattern: { value: /^[0-9]+$/, message: "Years in Insolvency and turnarounds must be a valid integer" }
                }}
                error={errors.industryExperienceYears?.message}
            />
            <TextAreaField 
                label="In no more than 250 words, please explain your experience in cross-border insolvency and turnarounds"
                name="industryExperienceSummary"
                register={register}
                rules={{ required: "Cross-border insolvency and turnarounds explanation is required", max: 250 }}
                error={errors.industryExperienceSummary?.message}
            />
          </>
      )
    },
    {
      title: "Educational and Professional Qualifications",
      content: (
        <>
          {educationFields.map((item, index) => (
            <div key={"education-" + index}>
              <div className="flex justify-between items-center">
                <h2 className="text-xl text-brandDarkBlue font-bold my-6">Education {index + 1}</h2>
                {index >= formData.course.courseEducation && (
                  <button type="button" onClick={() => removeEducation(index)} className="text-red underline font-medium whitespace-nowrap">
                    Remove
                  </button>
                )}
              </div>
              <InputField 
                  label="Degree"
                  name={`educationHistory[${index}].description`}
                  register={register}
                  rules={{ required: "Degree is required" }}
                  error={getErrorMessage("educationHistory", index, "description")}
              />
              <InputField 
                  label="Year awarded"
                  name={`educationHistory[${index}].yearAwarded`}
                  register={register}
                  rules={{ 
                    required: "Year awarded is required",
                    pattern: { value: /^[0-9]+$/, message: "Year awarded must be a valid integer" }
                  }}
                  error={getErrorMessage("educationHistory", index, "yearAwarded")}
              />
              <InputField 
                  label="College/University"
                  name={`educationHistory[${index}].collegeUniversity`}
                  register={register}
                  rules={{ required: "College/University is required" }}
                  error={getErrorMessage("educationHistory", index, "collegeUniversity")}
              />
              <InputField 
                  label="Language of instruction"
                  name={`educationHistory[${index}].language`}
                  register={register}
                  rules={{ required: "Language of instruction is required" }}
                  error={getErrorMessage("educationHistory", index, "language")}
              />
              <hr className="my-6 border-t border-dashed" />
            </div>
          ))}
          <button type="button" onClick={addEducation} className="bg-brandMidBlue hover:bg-blue-700 text-white font-medium mb-6 py-2 px-6 rounded">
              Add Another
          </button>
          {formData?.course?.courseQualifications > 0 && (
            <>
            {professionalFields.map((item, index) => (
              <div key={"qualification-" + index}>
                <div className="flex justify-between items-center">
                  <h2 className="text-xl text-brandDarkBlue font-bold my-6">Professional Qualification {index + 1}</h2>
                  {index > 0 && (
                    <button type="button" onClick={() => removeProfessional(index)} className="text-red underline font-medium whitespace-nowrap">
                      Remove
                    </button>
                  )}
                </div>
                <InputField 
                    label="Qualification"
                    name={`qualifications[${index}].qualificationDescription`}
                    register={register}
                    rules={{ required: "Qualification is required" }}
                    error={errors?.qualifications?.[index]?.qualificationDescription?.message}
                />
                <CustomDatePicker
                    label="Date of Admission"
                    name={`qualifications[${index}].qualificationDate`}
                    register={register}
                    selectedDate={watch(`qualifications[${index}].qualificationDate`)}
                    setValue={setValue}
                />
                <InputField 
                    label="Profession"
                    name={`qualifications[${index}].profession`}
                    register={register}
                />
                <RadioField 
                    label="Have you been practicing?"
                    name={`qualifications[${index}].practicing`}
                    register={register}
                    options={[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ]}
                    //onChange={handleBeenPracticing}
                    //rules={{ required: "This field is required" }}
                    //error={errors.practicingNoPracticing?.message}
                />
                <hr className="my-6 border-t border-dashed" />
              </div>
            ))}
            <button type="button" onClick={addProfessional} className="bg-brandMidBlue hover:bg-blue-700 text-white font-medium mb-6 py-2 px-6 rounded">
                Add Another
            </button>
          </>
          )}
          {referencesFields.map((item, index) => (
            <div key={"reference-" + index}>
              <div className="flex justify-between items-center">
                <h2 className="text-xl text-brandDarkBlue font-bold my-6">Referee {index + 1}</h2>
              </div>
              <InputField
                label="Referee title"
                name={`references[${index}].title`}
                register={register}
                rules={{ required: "Referee title is required" }}
                error={getErrorMessage("references", index, "titleId")}
              />
              <InputField 
                  label="Referee first name"
                  name={`references[${index}].firstName`}
                  register={register}
                  rules={{ required: "Referee first name is required" }}
                  error={getErrorMessage("references", index, "firstName")}
              />
              <InputField 
                  label="Referee last name"
                  name={`references[${index}].lastName`}
                  register={register}
                  rules={{ required: "Referee last name is required" }}
                  error={getErrorMessage("references", index, "lastName")}
              />
              <InputField 
                  label="Referee email"
                  name={`references[${index}].email`}
                  register={register}
                  rules={{ 
                    required: "Referee email is required",
                    pattern: { value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, message: "Email address must be a valid email address" }
                  }}
                  error={getErrorMessage("references", index, "email")}
              />
              <InputField 
                  label="Referee tel country code"
                  name={`references[${index}].phoneCountryCode`}
                  register={register}
              />
              <InputField 
                  label="Referee phone"
                  name={`references[${index}].phone`}
                  register={register}
              />
              <InputField 
                  label="Referee home address 1"
                  name={`references[${index}].address1`}
                  register={register}
              />
              <InputField 
                  label="Referee home address 2"
                  name={`references[${index}].address2`}
                  register={register}
              />
              <InputField 
                  label="Referee city"
                  name={`references[${index}].city`}
                  register={register}
              />
              <InputField 
                  label="Referee state"
                  name={`references[${index}].state`}
                  register={register}
              />
              <InputField 
                  label="Referee postcode"
                  name={`references[${index}].postcode`}
                  register={register}
              />
              {referencesFields.length > index+1 && <hr className="my-6 border-t border-dashed" />}
            </div>
          ))}
        </>
      )
    },
    {
      title: "Modules",
      conditional: !areAllModulesRequired,
      content: (
        <>
              <RadioField 
                  label="Number of years you require to finish the course"
                  name="numberOfYears"
                  register={register}
                  options={[
                    { value: 'accountant', label: 'I am to take all modules required over the course of one year' },
                    { value: 'lawyer', label: 'I aim to spread the modules over the course of two years and this is my first year of enrolment' }
                  ]}
              />
              <h2 className="text-xl text-brandDarkBlue font-bold mb-6">Compulsory Modules</h2>
              <h4 className="text-brandMidBlue font-bold mb-3">Module 1</h4>
              <CheckboxField
                  label="Module 1 - Introduction to International Insolvency Law"
                  name="module1"
                  fontWeight="normal"
                  checked={true}
                  register={register}
              />
              <h4 className="text-brandMidBlue font-bold mb-3">Module 2</h4>
              <CheckboxField
                  label="Module 2A - The UNCITRAL Model Laws Related to Insolvency"
                  name="module2a"
                  onChange={handleModule2AChange}
                  fontWeight="normal"
                  checked={module2 === '2A'}
                  register={register}
              />
              <CheckboxField
                  label="Module 2B - The European Insolvency Regulation"
                  name="module2b"
                  onChange={handleModule2BChange}
                  fontWeight="normal"
                  checked={module2 === '2B'}
                  register={register}
              />
              <h4 className="text-brandMidBlue font-bold mb-3">Module 3</h4>
              <CheckboxField
                  label="Module 3A - Insolvency System of the United States"
                  name="module3a"
                  onChange={handleDifferentEmailChecked}
                  fontWeight="normal"
                  register={register}
              />
              <CheckboxField
                  label="Module 3B - Insolvency System of the United Kingdom (England and Wales)"
                  name="module3b"
                  onChange={handleDifferentEmailChecked}
                  fontWeight="normal"
                  register={register}
              />
              <h2 className="text-xl text-brandDarkBlue font-bold mb-6">Elective Modules</h2>
              <RadioField 
                  label="Module 2"
                  name="module2"
                  register={register}
                  options={[
                    { value: 'accountant', label: 'Module 2B - The European Insolvency Regulation' }
                  ]}
              />
              <RadioField 
                  label="Module 3"
                  name="module3"
                  register={register}
                  options={[
                    { value: 'accountant', label: 'Module 3A - Insolvency System of the United States' }
                  ]}
              />
              <RadioField 
                  label="Module 4"
                  name="module4"
                  register={register}
                  options={[
                    { value: 'accountant', label: 'Module 4A - Argentina' },
                    { value: 'accountant', label: 'Module 4B - Brazil' },
                    { value: 'accountant', label: 'Module 4C - Canada' },
                    { value: 'accountant', label: 'Module 4D - Jamaica' },
                    { value: 'accountant', label: 'Module 4E - Mexico' },
                    { value: 'accountant', label: 'Module 4G - Chile' }
                  ]}
              />
              <RadioField 
                  label="Module 5"
                  name="module5"
                  register={register}
                  options={[
                    { value: 'accountant', label: 'Module 5A - Bermuda' },
                    { value: 'accountant', label: 'Module 5B - British Virgin Islands' },
                    { value: 'accountant', label: 'Module 5C - Cayman Islands' },
                    { value: 'accountant', label: 'Module 5D - Guernsey' },
                    { value: 'accountant', label: 'Module 5E - Jersey' },
                    { value: 'accountant', label: 'Module 5F - Mauritius' },
                    { value: 'accountant', label: 'Module 5G - The Bahamas' }
                  ]}
              />
              <RadioField 
                  label="Module 6"
                  name="module6"
                  register={register}
                  options={[
                    { value: 'accountant', label: 'Module 6A - France' },
                    { value: 'accountant', label: 'Module 6B - Germany' },
                    { value: 'accountant', label: 'Module 6C - Greece' },
                    { value: 'accountant', label: 'Module 6D - Italy' },
                    { value: 'accountant', label: 'Module 6E - The Netherlands' },
                    { value: 'accountant', label: 'Module 6F - Belgium' },
                    { value: 'accountant', label: 'Module 6G - Spain' }
                  ]}
              />
              <RadioField 
                  label="Module 7"
                  name="module7"
                  register={register}
                  options={[
                    { value: 'accountant', label: 'Module 7A - Israel' },
                    { value: 'accountant', label: 'Module 7B - Kenya' },
                    { value: 'accountant', label: 'Module 7C - Nigeria' },
                    { value: 'accountant', label: 'Module 7D - South Africa' },
                    { value: 'accountant', label: 'Module 7E - United Arab Emirates' },
                    { value: 'accountant', label: 'Module 7F - Uganda' },
                    { value: 'accountant', label: 'Module 7G - Morocco' }
                  ]}
              />
              <RadioField 
                  label="Module 8"
                  name="module8"
                  register={register}
                  options={[
                    { value: 'accountant', label: 'Module 8A - Australia' },
                    { value: 'accountant', label: 'Module 8B - China (PRC)' },
                    { value: 'accountant', label: 'Module 8C - Hong Kong' },
                    { value: 'accountant', label: 'Module 8D - India' },
                    { value: 'accountant', label: 'Module 8E - Singapore' },
                    { value: 'accountant', label: 'Module 8F - New Zealand' },
                    { value: 'accountant', label: 'Module 8G - Japan' }
                  ]}
              />
              <RadioField 
                  label="Module 9"
                  name="module9"
                  register={register}
                  options={[
                    { value: 'accountant', label: 'Module 9 - Ethics and Professional Practice' }
                  ]}
              />
            </>
      )
    },
    {
      title: "Payment",
      content: (
        <>
              <h2 className="text-xl text-brandDarkBlue font-bold mb-6">Certification</h2>
              <p className="text-brandMidBlue text-md mb-4">By clicking the box below, you certify that all the information you have provided in this application is true and correct. If you are found to have submitted incomplete, untrue or incorrect information, INSOL reserves the right to immediately cancel your enrolment on the course and in such circumstances no refund of the course fee will be made.</p>
              <CheckboxField
                  label="I certify that the information I have provided is true and correct"
                  name="infoCertify"
                  type="checkbox"
                  register={register}
                  rules={{ required: "Please certify that the information provided is true and correct" }}
                  error={errors.infoCertify?.message}
                  onChange={(e) => setValue("infoCertify", e.target.checked ? 1 : 0, { shouldValidate: true })}
              />
              <h2 className="text-xl text-brandDarkBlue font-bold mb-6">Terms and Conditions</h2>
              <div className="h-48 overflow-auto bg-white p-6 mb-6 border border-brandDarkBlue" dangerouslySetInnerHTML={{ __html: formData.course.courseTerms }} />
              <CheckboxField
                  label="Please click here to confirm you have read and agree to our terms & conditions"
                  name="terms"
                  type="checkbox"
                  register={register}
                  rules={{ required: "Please acknowledge you have read and agreed to our terms & conditions" }}
                  error={errors.terms?.message}
                  onChange={(e) => setValue("terms", e.target.checked ? 1 : 0, { shouldValidate: true })}
              />
              <h2 className="text-xl text-brandDarkBlue font-bold mb-6">Register and Payment</h2>
              {formData.course.courseQuestionDiscountContent &&
              formData.course.courseQuestionDiscountContent.trim() !== "" &&
              formData.course.courseQuestionDiscount !== 0 && (
                <CheckboxField
                  label={formData.course.courseQuestionDiscountContent}
                  name="applyQuestionDiscount"
                  type="checkbox"
                  register={register}
                />
              )}
              <RadioField 
                  label="Who will pay?"
                  name="fundingId"
                  register={register}
                  options={formData.fundingTypes.map(ft => { return { value: ft.itemId, label: ft.description } })}
                  onChange={handleWhoWillPayOther}
                  rules={{ required: "Who will pay is required" }}
                  error={errors.fundingId?.message}
              />
              <p className="text-brandMidBlue text-md mb-4">Payment will not be taken until your place on the course has been confirmed.</p>
              <RadioField 
                label="Payment method"
                name="paymentMethod"
                register={register}
                options={[
                  { value: 'CARD', label: 'Debit or Credit Card' },
                  { value: 'BACS', label: 'BACS' }
                ]}
                onChange={handlePaymentMethod}
                rules={{ required: "Payment method is required" }}
                error={errors.paymentMethod?.message}
              />
              {paymentMethod === "BACS" && (
                <>
                <InputField 
                    label="Billing Name"
                    name="billingName"
                    register={register}
                    rules={{ required: "Billing Name is required" }}
                    error={errors.billingName?.message}
                />
                <InputField 
                    label="Billing Address 1"
                    name="billingAddress1"
                    register={register}
                    rules={{ required: "Billing Address 1 is required" }}
                    error={errors.billingAddress1?.message}
                />
                <InputField 
                    label="Billing Address 2"
                    name="billingAddress2"
                    register={register}
                />
                <InputField 
                    label="Billing City"
                    name="billingCity"
                    register={register}
                    rules={{ required: "Billing City is required" }}
                    error={errors.billingCity?.message}
                />
                <InputField 
                    label="Billing State"
                    name="billingState"
                    register={register}
                />
                <InputField 
                    label="Billing Postcode"
                    name="billingPostcode"
                    register={register}
                    rules={{ required: "Billing Postcode is required" }}
                    error={errors.billingPostcode?.message}
                />
                </>
              )}
              <CheckboxField
                label="Apply bursary discount"
                name="applyBursaryDiscount"
                register={register}
                onChange={handleBursaryDiscount}
            />
            {isBursaryDiscountChecked && (
              <InputField 
                  label="Enter discount code here"
                  name="discountCode"
                  register={register}
              />
            )}
            </>
      )
    }
  ]

  const filteredSteps = steps.filter(step => step.conditional === undefined || step.conditional);

  const formatDate = (dateString) => {
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-GB', options);
  };

  if (formData.course.courseId) {
  const courseEnrollmentStartDate = new Date(formData.course.courseEnrollmentStartDate);
  const courseEnrollmentEndDate = new Date(formData.course.courseEnrollmentEndDate);
  const today = new Date();

  console.log('courseEnrollmentStartDate:', courseEnrollmentStartDate);
  console.log('courseEnrollmentEndDate:', courseEnrollmentEndDate);
  console.log('today:', today);

  const isBetweenDates = today >= courseEnrollmentStartDate && today <= courseEnrollmentEndDate;
  
  if (false && formHidden) {
    return (
      <div>
      {/* Hero */}
      <section className="hero hero--education relative h-80 bg-gradient-to-r from-darkHero to-lightHero text-white">
        <div className="container relative z-10 flex flex-col h-full mx-auto max-w-540 md:max-w-640 lg:max-w-992 xl:max-w-90rem p-4">
          <header className="mt-auto mb-10">
            <h2 className="text-4.5xl text-white font-bold">{formData.course.courseTitle}</h2>
          </header>
          <div className="breadcrumbs mb-4">
            <ul className="flex">
              <li><Link className="white underline" to="/">Home</Link></li>
              <span className="breadcrumb-seperator mx-1">\</span>
              <li><Link className="white underline" to="/education/v1">Education</Link></li>
              <li className="breadcrumb-current">
                <span className="breadcrumb-seperator mx-1">\</span><span className="text-fontColor">{formData.course.courseTitle}</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="flex absolute top-0 left-0 right-0 bottom-0">
          <img className="object-cover lg:object-contain lg:object-right ml-auto" src="https://insol.azureedge.net/cmsstorage/insol/media/heros/hero-arrow-education.png" alt="" />
        </div>
      </section>
      <section className="courses py-8 lg:py-12">
        <div className="flex flex-col h-full mx-auto max-w-540 md:max-w-640 lg:max-w-760">
            <div className='h-full text-sm p-20 w-full bg-brandGrey'>
              <h2 className="text-xl lg:text-4xl text-brandDarkBlue font-bold mb-6">Application</h2>
              <p className="text-brandMidBlue text-md mb-4">It looks like you have already applied to this course.</p>
            </div>
        </div>
      </section>
      </div>
    )
  }
  
  if (!isBetweenDates) {
    return (
      <div>
      {/* Hero */}
      <section className="hero hero--education relative h-80 bg-gradient-to-r from-darkHero to-lightHero text-white">
        <div className="container relative z-10 flex flex-col h-full mx-auto max-w-540 md:max-w-640 lg:max-w-992 xl:max-w-90rem p-4">
          <header className="mt-auto mb-10">
            <h2 className="text-4.5xl text-white font-bold">{formData.course.courseTitle}</h2>
          </header>
          <div className="breadcrumbs mb-4">
            <ul className="flex">
              <li><Link className="white underline" to="/">Home</Link></li>
              <span className="breadcrumb-seperator mx-1">\</span>
              <li><Link className="white underline" to="/education/v1">Education</Link></li>
              <li className="breadcrumb-current">
                <span className="breadcrumb-seperator mx-1">\</span><span className="text-fontColor">{formData.course.courseTitle}</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="flex absolute top-0 left-0 right-0 bottom-0">
          <img className="object-cover lg:object-contain lg:object-right ml-auto" src="https://insol.azureedge.net/cmsstorage/insol/media/heros/hero-arrow-education.png" alt="" />
        </div>
      </section>
      <section className="courses py-8 lg:py-12">
        <div className="flex flex-col h-full mx-auto max-w-540 md:max-w-640 lg:max-w-760">
            <div className='h-full text-sm p-20 w-full bg-brandGrey'>
              <h2 className="text-xl lg:text-4xl text-brandDarkBlue font-bold mb-6">Applications not open</h2>
              <p className="text-brandMidBlue text-md mb-4">Enrolment for the course is between {formatDate(formData.course.courseEnrollmentStartDate)} - {formatDate(formData.course.courseEnrollmentEndDate)}.</p>
            </div>
        </div>
      </section>
      </div>

    )}
  }

  return (
    <div>
      {/* Hero */}
      <section className="hero hero--education relative h-80 bg-gradient-to-r from-darkHero to-lightHero text-white">
        <div className="container relative z-10 flex flex-col h-full mx-auto max-w-540 md:max-w-640 lg:max-w-992 xl:max-w-90rem p-4">
          <header className="mt-auto mb-10">
            <h2 className="text-4.5xl text-white font-bold">{formData.course.courseTitle}</h2>
          </header>
          {/* Breadcrumbs */}
          <div className="breadcrumbs mb-4">
            <ul className="flex">
              <li><Link className="white underline" to="/">Home</Link></li>
              <span className="breadcrumb-seperator mx-1">\</span>
              <li><Link className="white underline" to="/education/v1">Education</Link></li>
              <li className="breadcrumb-current">
                <span className="breadcrumb-seperator mx-1">\</span><span className="text-fontColor">{formData.course.courseTitle}</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="flex absolute top-0 left-0 right-0 bottom-0">
          <img className="object-cover lg:object-contain lg:object-right ml-auto" src="https://insol.azureedge.net/cmsstorage/insol/media/heros/hero-arrow-education.png" alt="" />
        </div>
      </section>

      {/* My Courses */}
      <section className="courses py-8 lg:py-12" ref={formRef}>
        <div className="flex flex-col h-full mx-auto max-w-540 md:max-w-640 lg:max-w-760">
          {isSubmitted ? (
            <div className='h-full text-sm p-20 w-full bg-brandGrey'>
              <h2 className="text-xl lg:text-4xl text-brandDarkBlue font-bold mb-6">Confirmation</h2>
              <h2 className="text-xl text-brandDarkBlue font-bold mb-6">Thank you</h2>
              <p className="text-brandMidBlue text-md mb-4">Your application is now in review by the INSOL team.</p>
            </div>
          ) : (
            <>
              <StepProgressBar step={step + 1} totalSteps={filteredSteps.length} onBack={handleBack} />
              <div className='h-full text-sm p-20 w-full bg-brandGrey'>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="flex items-center justify-between mb-6">
                      {step > 0 && (
                        <button type="button" onClick={handleBack} className="flex text-brandMidBlue underline">
                          Back
                        </button>
                      )}
                    </div>
                    <h2 className="text-xl lg:text-4xl text-brandDarkBlue font-bold mb-6">
                      {step + 1} of {filteredSteps.length}: {filteredSteps[step].title}
                    </h2>
                    {filteredSteps[step].content}
                    <div className="flex items-center justify-between mt-6">
                      {step > 0 && (
                        <button type="button" onClick={handleBack} className="flex text-brandMidBlue underline">
                          Back
                        </button>
                      )}
                      <button
                        type="submit"
                        className={`${isSubmitting ? "bg-secondary-200" : "bg-brandMidBlue"} hover:bg-blue-700 text-white font-medium py-2 px-6 rounded ml-auto`}
                        disabled={isSubmitting}
                      >
                        {step < filteredSteps.length - 1 ? 'Continue' : isSubmitting ? 'Submitting...' : 'Submit'}
                      </button>
                    </div>
                  </form>
              </div>
            </>
          )}
          {formError && (
            <Modal
                isOpen={showFormErrorModal}
                onClose={handleCloseFormErrorModal}
                className="max-w-xl"
            >
                <div className="p-6">
                    <h2 className="text-lg font-bold mb-4">Sorry, there was an error</h2>
                    <p>It looks like you may have missed a required filled, please go back and try again.</p>
                    <pre>{formError}</pre>
                    <div className="flex justify-end mt-4">
                        <button
                            onClick={handleCloseFormErrorModal}
                            className="bg-kenticoblue hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-full"
                        >
                            Continue
                        </button>
                    </div>
                </div>
            </Modal>
          )}
          {paymentPreview && (
            <Modal
                isOpen={showPaymentPreviewModal}
                onClose={handleClosePaymentPreviewModal}
                size="max-w-sm"
            >
                <div className="p-6">
                    <h2 className="text-lg font-bold mb-4">Your Course Price</h2>
                    <p className="mb-4">If the course price is not what you expected, please e-mail <a className="underline" href={formData.course.courseAdminName}>{formData.course.courseAdminName}</a>.</p>
                    <table className="table-auto w-full mb-4">
                        <thead>
                            <tr>
                                <td className="py-2 text-left">Course</td>
                                <td className="py-2 text-left">£{paymentPreview.price}</td>
                            </tr>
                            {paymentPreview.discount > 0 && (
                                <tr>
                                    <td className="py-2 text-left">Discount</td>
                                    <td className="py-2 text-left">£{paymentPreview.discount}</td>
                                </tr>
                            )}
                            <tr className="border-t">
                                <td className="py-2 text-left">Total</td>
                                <td className="py-2 text-left">£{paymentPreview.totalAmountDue}</td>
                            </tr>
                        </thead>
                    </table>
                    <div className="flex justify-end mt-4">
                        <button
                            onClick={handleClosePaymentPreviewModal}
                            className="bg-uidark hover:bg-blue-700 text-uidarker font-medium py-2 px-6 mr-3 rounded-full"
                        >
                            Go back
                        </button>
                        <button
                            onClick={handleContinuePayment}
                            className="bg-kenticoblue hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-full"
                        >
                            Continue
                        </button>
                    </div>
                </div>
            </Modal>
          )}
        </div>
      </section>

    </div>
  );
};

export default CourseApplication;